import React, { useEffect, useState, useMemo } from 'react';
import { startCase } from "lodash";
import { Button, Card, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import pluralize from 'pluralize';
import { useEffectOnce } from 'usehooks-ts';
import { boolean, object, string } from 'yup';
import { Form, Formik } from 'formik';
import DataTable from 'react-data-table-component';
import { TableColumn } from 'react-data-table-component';

import { selectIsFrontline } from "@components/Auth";
import { OrderStatuses } from "@models/Order";
import { formatDateTime } from '@utils/datetime';
import { tImportStatuses, ImportTypes, tImportTypes, ImportModel } from '@models/ImportModel';
import { CreateImportCorporateHealthQuoteParams } from '@apis/CorporateHealthQuoteApi';
import { yupCsvFile, yupFile } from '@utils/yup';
import { DownloadLinkButton, FormGroupCheck, FormGroupControl, FormGroupSelect } from '@components/Base';
import { CorporateHealthOrder } from '@models/CorporateHealthOrder';

import { selectCorporateHealthQuoteBuy } from "../selector";
import {
  corporateHealthQuoteCreateImport,
  CorporateHealthQuoteCreateImportPayload,
  corporateHealthQuoteGetImports,
  CorporateHealthQuoteGetImportsPayload,
  stateUpdate,
  StateUpdatePayload
} from '../action';
import { dUserName } from '@models/User';

const GROUP_PROPS = { className: 'mb-3' };

export type FormValues = Omit<CreateImportCorporateHealthQuoteParams, 'orderNumber'>;

const customStyles = {
  headCells: {
    style: {
      fontWeight: 'bold',
    },
  },
};

export const useValidationSchema = () => {
  const { t } = useTranslation();

  return object({
    agree: boolean().oneOf([true], 'You must agree'),
    importType: string().required(t('validation:required')),
    dataCsv: yupCsvFile(),
    proof: yupFile().optional(),
  });
};

type Props = Pick<CorporateHealthOrder, 'orderNumber' | 'status'>;

const ImportCardBody = ({ orderNumber, status }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isFrontline = useSelector(selectIsFrontline);
  const { imports } = useSelector(selectCorporateHealthQuoteBuy);
  const validationSchema = useValidationSchema();
  const [showDialog, setShowDialog] = useState(false);

  const isPending = status === OrderStatuses.Pending;
  const isLocked = status === OrderStatuses.Locked;
  const isActive = status === OrderStatuses.Active;
  const shouldAddImport = isFrontline && (isPending || isLocked || isActive);

  const columns = useMemo(() => [
    {
      name: t('common:type'),
      selector: (iimport) => tImportTypes(iimport.importType),
    },
    {
      name: t('common:createdDate'),
      selector: (iimport) => formatDateTime(iimport.createdAt),
      minWidth: '180px',
    },
    {
      name: t('common:creator'),
      selector: (iimport) => dUserName(iimport.creator),
    },
    {
      name: t('common:status'),
      selector: (iimport) => tImportStatuses(iimport.status),
    },
    {
      name: t('common:success'),
      selector: (iimport) => iimport.successCount,
    },
    {
      name: t('common:fail'),
      selector: (iimport) => iimport.errorsCount,
    },
    {
      name: t('common:skip'),
      selector: (iimport) => iimport.skipsCount,
    },
    {
      name: t('common:data'),
      selector: (iimport) => <DownloadLinkButton href={iimport.dataCsv} />,
    },
    {
      name: t('common:result'),
      selector: (iimport) => <DownloadLinkButton href={iimport.resultCsv} />,
    },
    {
      name: t('common:proof'),
      selector: (iimport) => <DownloadLinkButton href={iimport.proof} />,
    },
  ] as TableColumn<ImportModel>[], []);

  const handleSubmit = (values: FormValues) => {
    const payload: CorporateHealthQuoteCreateImportPayload = { orderNumber, ...values };
    dispatch(corporateHealthQuoteCreateImport(payload));
    setShowDialog(false);
  }

  useEffect(() => {
    if (!orderNumber) return;
    const payload: CorporateHealthQuoteGetImportsPayload = { orderNumber };
    dispatch(corporateHealthQuoteGetImports(payload));
  }, [orderNumber]);

  useEffectOnce(() => {
    return () => {
      const payload: StateUpdatePayload = { imports: [] };
      dispatch(stateUpdate(payload));
    };
  });

  return (
    <>
      <Card.Body>
        <Card.Title className="d-flex flex-row justify-content-between align-items-center">
          {pluralize(t('common:import'), 2)} ({imports.length})
          {shouldAddImport && (
            <Button onClick={() => setShowDialog(true)} variant="outline-primary">
              {startCase(t('common:addImport'))}
            </Button>
          )}
        </Card.Title>

        <DataTable
          columns={columns}
          data={imports ?? []}
          customStyles={customStyles}
          fixedHeader
        />
      </Card.Body>

      <Modal
        show={showDialog}
        onHide={() => setShowDialog(false)}
      >
        <Formik
          initialValues={{ agree: false, dataCsv: undefined, importType: '' }}
          onSubmit={handleSubmit}
          validationSchema={validationSchema}
        >
          <Form>
            <Modal.Header closeButton>
              <Modal.Title>Import</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <FormGroupSelect name="importType" label={t('common:importType')} groupProps={GROUP_PROPS}>
                <option value=""></option>
                <option value={ImportTypes.AddUsers}>{tImportTypes(ImportTypes.AddUsers)}</option>
                <option value={ImportTypes.DeactivateUsers}>{tImportTypes(ImportTypes.DeactivateUsers)}</option>
              </FormGroupSelect>

              <FormGroupControl name="dataCsv" type="file" label={t('common:csvFile')} groupProps={GROUP_PROPS} />
              <FormGroupControl name="proof" type="file" label={t('common:proof')} groupProps={GROUP_PROPS} />
              <FormGroupCheck name="agree" groupProps={GROUP_PROPS} label="Agree" />
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowDialog(false)}>
                {t('confirmModal.cancel')}
              </Button>
              <Button type="submit" variant="primary">
                {t('confirmModal.confirm')}
              </Button>
            </Modal.Footer>
          </Form>
        </Formik>
      </Modal>
    </>
  )
};

export { ImportCardBody };
